import RouteErrorPage from './pages/RouteErrorPage';
import { PageLoader } from './components/ui/page-loader';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { RequireAuth } from './components/auth/RequireAuth';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const OrderLinesPage = lazy(() => import('./pages/admin/OrderLinesPage'));
const OrderIntakePage = lazy(() => import('./pages/admin/order-intake/OrderIntakePage'));
const InventoryPage = lazy(() => import('./pages/admin/inventory/InventoryPage'));
const ProcurementPage = lazy(() => import('./pages/admin/procurement/ProcurementPage'));
const DeliveryTablesPage = lazy(() => import('./pages/admin/procurement/DeliveryTablesPage'));
const ShipmentsPage = lazy(() => import('./pages/admin/shipment-tracking/ShipmentsPage'));
const ShipmentOperationsPage = lazy(
  () => import('./pages/admin/shipment-tracking/ShipmentOperationsPage')
);
const TemperaturePage = lazy(() => import('./pages/admin/temperature/TemperaturePage'));
const WarehousePage = lazy(() => import('./pages/admin/warehouse/WarehousePage'));
const BillingPage = lazy(() => import('./pages/admin/billing/BillingPage'));
const BillingSetupPage = lazy(() => import('./pages/admin/billing/BillingSetupPage'));
const AuditPage = lazy(() => import('./pages/admin/AuditPage'));
// UC-03: Lazy-loaded ProductsPage — emitted as a separate chunk.
const MasterDataPage = lazy(() => import('./pages/admin/masterdata/catalog/MasterDataPage'));
const ProductsPage = lazy(() => import('./pages/admin/masterdata/ProductsPage'));
const ConfigurationPage = lazy(() => import('./pages/admin/configuration/ConfigurationPage'));
const ImportReviewPage = lazy(() => import('./pages/admin/order-intake/ImportReviewPage'));
const WarningsPage = lazy(() => import('./pages/admin/warnings/WarningsPage'));
const BlockedStockPage = lazy(() => import('./pages/admin/inventory/BlockedStockPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const basename = import.meta.env.VITE_ROUTER_BASENAME ?? '/';

export const router = createBrowserRouter(
  [
    {
      path: '/login',
      errorElement: <RouteErrorPage />,
      element: (
        <Suspense fallback={<PageLoader />}>
          <LoginPage />
        </Suspense>
      ),
    },
    {
      path: '/forgot-password',
      errorElement: <RouteErrorPage />,
      element: (
        <Suspense fallback={<PageLoader />}>
          <ForgotPasswordPage />
        </Suspense>
      ),
    },
    {
      path: '/reset-password',
      errorElement: <RouteErrorPage />,
      element: (
        <Suspense fallback={<PageLoader />}>
          <ResetPasswordPage />
        </Suspense>
      ),
    },
    {
      path: '/',
      errorElement: <RouteErrorPage />,
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      children: [
        {
          path: 'admin/configuration',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ConfigurationPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/warnings',
          element: (
            <Suspense fallback={<PageLoader />}>
              <WarningsPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/blocked-stock',
          element: (
            <Suspense fallback={<PageLoader />}>
              <BlockedStockPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/order-intake/review',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ImportReviewPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/delivery-tables/changes',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ImportReviewPage delivery />
            </Suspense>
          ),
        },
        {
          index: true,
          element: <Navigate to="/admin/masterdata" replace />,
        },
        {
          path: 'admin/dashboard',
          element: <Navigate to="/admin/masterdata" replace />,
        },
        {
          path: 'admin/users',
          element: (
            <Suspense fallback={<PageLoader />}>
              <AdminUsersPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/roles',
          element: (
            <Suspense fallback={<PageLoader />}>
              <RolesPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/order-lines',
          element: (
            <Suspense fallback={<PageLoader />}>
              <OrderLinesPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/order-intake',
          element: (
            <Suspense fallback={<PageLoader />}>
              <OrderIntakePage />
            </Suspense>
          ),
        },
        {
          path: 'admin/inventory-planning',
          element: (
            <Suspense fallback={<PageLoader />}>
              <InventoryPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/inventory-planning/products/:productId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <InventoryPage />
            </Suspense>
          ),
        },
        { path: 'admin/inventory', element: <Navigate to="/admin/inventory-planning" replace /> },
        {
          path: 'admin/shipment-operations',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ShipmentOperationsPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/temperature',
          element: (
            <Suspense fallback={<PageLoader />}>
              <TemperaturePage />
            </Suspense>
          ),
        },
        {
          path: 'admin/warehouse',
          element: (
            <Suspense fallback={<PageLoader />}>
              <WarehousePage />
            </Suspense>
          ),
        },
        {
          path: 'admin/billing',
          element: (
            <Suspense fallback={<PageLoader />}>
              <BillingPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/billing-setup',
          element: (
            <Suspense fallback={<PageLoader />}>
              <BillingSetupPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/procurement',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ProcurementPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/delivery-tables',
          element: (
            <Suspense fallback={<PageLoader />}>
              <DeliveryTablesPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/shipments',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ShipmentsPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/audit',
          element: (
            <Suspense fallback={<PageLoader />}>
              <AuditPage />
            </Suspense>
          ),
        },
        // MF3: /admin/masterdata/products route, matching the nav entry's
        // path exactly (nissei-frontend-02-masterdata.md).
        {
          path: 'admin/masterdata/products',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ProductsPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/masterdata',
          element: (
            <Suspense fallback={<PageLoader />}>
              <MasterDataPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/masterdata/:entityId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <MasterDataPage />
            </Suspense>
          ),
        },
        {
          path: 'admin/*',
          element: (
            <Suspense fallback={<PageLoader />}>
              <NotFoundPage />
            </Suspense>
          ),
        },
      ],
    },
  ],
  { basename }
);
